import { useState, useEffect, useRef, useCallback } from 'react'
import Button from '@/components/ui/Button'
import api from '@/services/api'
import {
  createTrainingPlan,
  getClientPlans,
  updatePlanStatus,
  deleteTrainingPlan,
  updateTrainingPlan,
  fileToBase64,
  TrainingPlan,
  Exercise,
  UpdatePlanData,
} from '@/services/trainingPlans'
import {
  freezeClient,
  unfreezeClient,
  blockClient,
  unblockClient,
} from '@/services/trainers'

type Tab = 'active' | 'pending' | 'frozen' | 'blocked'

interface Client {
  id: string
  user: {
    id: string
    first_name: string
    last_name?: string
    username?: string
  }
  status: 'trial' | 'approved' | 'frozen' | 'blocked'
  notes?: string
  created_at: string
  freeze_until?: string
  freeze_reason?: string
  frozen_at?: string
  blocked_at?: string
}

interface PendingClient {
  id: string
  client_id: string
  user: {
    first_name: string
    last_name?: string
    username?: string
  }
  created_at: string
}

export default function TrainerClients() {
  const [activeTab, setActiveTab] = useState<Tab>('active')
  const [clients, setClients] = useState<Client[]>([])
  const [pendingClients, setPendingClients] = useState<PendingClient[]>([])
  const [frozenClients, setFrozenClients] = useState<Client[]>([])
  const [blockedClients, setBlockedClients] = useState<Client[]>([])
  const [isLoading, setIsLoading] = useState(true)

  // Состояние для модального окна создания плана
  const [showCreatePlanModal, setShowCreatePlanModal] = useState(false)
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null)
  const [selectedClientName, setSelectedClientName] = useState<string>('')

  // Состояние для просмотра планов клиента
  const [showPlansModal, setShowPlansModal] = useState(false)
  const [clientPlans, setClientPlans] = useState<TrainingPlan[]>([])
  const [isLoadingPlans, setIsLoadingPlans] = useState(false)

  // Состояние для модального окна заморозки
  const [showFreezeModal, setShowFreezeModal] = useState(false)

  // Состояние для модального окна блокировки
  const [showBlockModal, setShowBlockModal] = useState(false)

  // Состояние для invite-ссылки
  const [inviteLink, setInviteLink] = useState<string>('')
  const [copied, setCopied] = useState(false)

  // Загрузка клиентов
  const loadData = useCallback(async () => {
    setIsLoading(true)
    try {
      const [activeResponse, pendingResponse, frozenResponse, blockedResponse] = await Promise.all([
        api.get('/trainers/me/clients'),
        api.get('/trainers/me/clients/pending'),
        api.get('/trainers/me/clients', { params: { status: 'frozen' } }),
        api.get('/trainers/me/clients', { params: { status: 'blocked' } }),
      ])
      setClients(activeResponse.data.clients || [])
      setPendingClients(pendingResponse.data.pending || [])
      setFrozenClients(frozenResponse.data.clients || [])
      setBlockedClients(blockedResponse.data.clients || [])
    } catch (err) {
      console.error('Error loading clients:', err)
    } finally {
      setIsLoading(false)
    }

    // Загружаем invite-link отдельно чтобы ошибка не влияла на остальные данные
    try {
      const inviteResponse = await api.get('/trainers/me/invite-link')
      console.log('Invite response:', inviteResponse.data)
      setInviteLink(inviteResponse.data.invite_link || '')
    } catch (err) {
      console.error('Error loading invite link:', err)
    }
  }, [])

  useEffect(() => {
    loadData()
  }, [loadData])

  // Refetch when app becomes visible (Telegram Mini App caching)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        loadData()
      }
    }
    document.addEventListener('visibilitychange', handleVisibility)
    return () => document.removeEventListener('visibilitychange', handleVisibility)
  }, [loadData])

  const handleApprove = async (clientId: string) => {
    try {
      await api.post(`/trainers/me/clients/${clientId}/approve`)
      loadData()
    } catch (err) {
      console.error('Error approving client:', err)
    }
  }

  const handleApproveTrial = async (clientId: string) => {
    try {
      await api.post(`/trainers/me/clients/${clientId}/approve-trial`)
      loadData()
    } catch (err) {
      console.error('Error approving client for trial:', err)
    }
  }

  const handleReject = async (clientId: string) => {
    try {
      await api.post(`/trainers/me/clients/${clientId}/reject`)
      loadData()
    } catch (err) {
      console.error('Error rejecting client:', err)
    }
  }

  const handleOpenCreatePlan = (clientId: string, clientName: string) => {
    setSelectedClientId(clientId)
    setSelectedClientName(clientName)
    setShowCreatePlanModal(true)
  }

  const handleOpenPlans = async (clientId: string, clientName: string) => {
    setSelectedClientId(clientId)
    setSelectedClientName(clientName)
    setIsLoadingPlans(true)
    setShowPlansModal(true)

    try {
      const response = await getClientPlans(clientId)
      setClientPlans(response.plans)
    } catch (err) {
      console.error('Error loading plans:', err)
    } finally {
      setIsLoadingPlans(false)
    }
  }

  const handlePlanCreated = () => {
    setShowCreatePlanModal(false)
    // Можно показать уведомление об успехе
  }

  const handleStatusChange = async (planId: string, newStatus: 'active' | 'inactive') => {
    try {
      await updatePlanStatus(planId, newStatus)
      if (selectedClientId) {
        const response = await getClientPlans(selectedClientId)
        setClientPlans(response.plans)
      }
    } catch (err) {
      console.error('Error updating status:', err)
    }
  }

  const handleDeletePlan = async (planId: string) => {
    if (!confirm('Удалить план тренировок?')) return

    try {
      await deleteTrainingPlan(planId)
      if (selectedClientId) {
        const response = await getClientPlans(selectedClientId)
        setClientPlans(response.plans)
      }
    } catch (err) {
      console.error('Error deleting plan:', err)
    }
  }

  const handleUpdatePlan = async (planId: string, data: UpdatePlanData) => {
    try {
      await updateTrainingPlan(planId, data)
      if (selectedClientId) {
        const response = await getClientPlans(selectedClientId)
        setClientPlans(response.plans)
      }
    } catch (err) {
      console.error('Error updating plan:', err)
      throw err
    }
  }

  // Обработчики для заморозки/блокировки
  const handleOpenFreeze = (clientId: string, clientName: string) => {
    setSelectedClientId(clientId)
    setSelectedClientName(clientName)
    setShowFreezeModal(true)
  }

  const handleFreeze = async (days?: number, reason?: string) => {
    if (!selectedClientId) return
    try {
      await freezeClient(selectedClientId, { days, reason })
      setShowFreezeModal(false)
      loadData()
    } catch (err) {
      console.error('Error freezing client:', err)
    }
  }

  const handleUnfreeze = async (clientId: string) => {
    try {
      await unfreezeClient(clientId)
      loadData()
    } catch (err) {
      console.error('Error unfreezing client:', err)
    }
  }

  const handleOpenBlock = (clientId: string, clientName: string) => {
    setSelectedClientId(clientId)
    setSelectedClientName(clientName)
    setShowBlockModal(true)
  }

  const handleBlock = async (reason?: string) => {
    if (!selectedClientId) return
    try {
      await blockClient(selectedClientId, { reason })
      setShowBlockModal(false)
      loadData()
    } catch (err) {
      console.error('Error blocking client:', err)
    }
  }

  const handleUnblock = async (clientId: string) => {
    try {
      await unblockClient(clientId)
      loadData()
    } catch (err) {
      console.error('Error unblocking client:', err)
    }
  }

  const handleCopyInviteLink = async () => {
    if (!inviteLink) return
    try {
      await navigator.clipboard.writeText(inviteLink)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      // Fallback для мобильных браузеров
      const textArea = document.createElement('textarea')
      textArea.value = inviteLink
      textArea.style.position = 'fixed'
      textArea.style.left = '-999999px'
      document.body.appendChild(textArea)
      textArea.select()
      try {
        document.execCommand('copy')
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      } catch {
        console.error('Failed to copy')
      }
      document.body.removeChild(textArea)
    }
  }

  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold text-tg-text mb-4">Клиенты</h1>

      {/* Табы */}
      <div className="flex gap-2 mb-6 flex-wrap">
        <button
          onClick={() => setActiveTab('active')}
          className={`flex-1 min-w-[80px] py-2 rounded-lg font-medium transition-all text-sm ${
            activeTab === 'active'
              ? 'bg-tg-button text-tg-button-text'
              : 'bg-tg-secondary text-tg-hint'
          }`}
        >
          Активные
          {clients.length > 0 && ` (${clients.length})`}
        </button>
        <button
          onClick={() => setActiveTab('pending')}
          className={`flex-1 min-w-[80px] py-2 rounded-lg font-medium transition-all relative text-sm ${
            activeTab === 'pending'
              ? 'bg-tg-button text-tg-button-text'
              : 'bg-tg-secondary text-tg-hint'
          }`}
        >
          Заявки
          {pendingClients.length > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
              {pendingClients.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('frozen')}
          className={`flex-1 min-w-[80px] py-2 rounded-lg font-medium transition-all text-sm ${
            activeTab === 'frozen'
              ? 'bg-tg-button text-tg-button-text'
              : 'bg-tg-secondary text-tg-hint'
          }`}
        >
          ❄️ {frozenClients.length > 0 && `(${frozenClients.length})`}
        </button>
        <button
          onClick={() => setActiveTab('blocked')}
          className={`flex-1 min-w-[80px] py-2 rounded-lg font-medium transition-all text-sm ${
            activeTab === 'blocked'
              ? 'bg-tg-button text-tg-button-text'
              : 'bg-tg-secondary text-tg-hint'
          }`}
        >
          🚫 {blockedClients.length > 0 && `(${blockedClients.length})`}
        </button>
      </div>

      {/* Ссылка для приглашения */}
      <div className="mb-6 p-4 bg-tg-secondary rounded-xl">
        <h3 className="font-medium text-tg-text mb-2">Пригласить клиента</h3>
        <p className="text-sm text-tg-hint mb-3">
          Поделитесь ссылкой с новыми клиентами
        </p>
        {inviteLink && (
          <p className="text-xs text-tg-hint mb-3 break-all">{inviteLink}</p>
        )}
        <Button
          variant="outline"
          fullWidth
          onClick={handleCopyInviteLink}
          disabled={!inviteLink}
        >
          {copied ? '✓ Скопировано!' : 'Скопировать ссылку'}
        </Button>
      </div>

      {/* Загрузка */}
      {isLoading && (
        <div className="text-center py-8 text-tg-hint">Загрузка...</div>
      )}

      {/* Список клиентов */}
      {!isLoading && activeTab === 'active' && (
        <div className="space-y-3">
          {clients.length === 0 ? (
            <div className="text-center py-8 text-tg-hint">
              У вас пока нет активных клиентов
            </div>
          ) : (
            clients.map((client) => (
              <ClientCard
                key={client.id}
                client={client}
                onApprove={client.status === 'trial' ? () => handleApprove(client.id) : undefined}
                onOpenCreatePlan={() =>
                  handleOpenCreatePlan(
                    client.id,
                    `${client.user.first_name} ${client.user.last_name || ''}`.trim()
                  )
                }
                onOpenPlans={() =>
                  handleOpenPlans(
                    client.id,
                    `${client.user.first_name} ${client.user.last_name || ''}`.trim()
                  )
                }
                onFreeze={() =>
                  handleOpenFreeze(
                    client.id,
                    `${client.user.first_name} ${client.user.last_name || ''}`.trim()
                  )
                }
                onBlock={() =>
                  handleOpenBlock(
                    client.id,
                    `${client.user.first_name} ${client.user.last_name || ''}`.trim()
                  )
                }
              />
            ))
          )}
        </div>
      )}

      {!isLoading && activeTab === 'pending' && (
        <div className="space-y-3">
          {pendingClients.length === 0 ? (
            <div className="text-center py-8 text-tg-hint">
              Нет заявок на подтверждение
            </div>
          ) : (
            pendingClients.map((pc) => (
              <PendingClientCard
                key={pc.id}
                client={pc}
                onApproveTrial={() => handleApproveTrial(pc.client_id)}
                onApprove={() => handleApprove(pc.client_id)}
                onReject={() => handleReject(pc.client_id)}
              />
            ))
          )}
        </div>
      )}

      {!isLoading && activeTab === 'frozen' && (
        <div className="space-y-3">
          {frozenClients.length === 0 ? (
            <div className="text-center py-8 text-tg-hint">
              Нет замороженных клиентов
            </div>
          ) : (
            frozenClients.map((client) => (
              <ClientCard
                key={client.id}
                client={client}
                onOpenCreatePlan={() =>
                  handleOpenCreatePlan(
                    client.id,
                    `${client.user.first_name} ${client.user.last_name || ''}`.trim()
                  )
                }
                onOpenPlans={() =>
                  handleOpenPlans(
                    client.id,
                    `${client.user.first_name} ${client.user.last_name || ''}`.trim()
                  )
                }
                onUnfreeze={() => handleUnfreeze(client.id)}
                onBlock={() =>
                  handleOpenBlock(
                    client.id,
                    `${client.user.first_name} ${client.user.last_name || ''}`.trim()
                  )
                }
              />
            ))
          )}
        </div>
      )}

      {!isLoading && activeTab === 'blocked' && (
        <div className="space-y-3">
          {blockedClients.length === 0 ? (
            <div className="text-center py-8 text-tg-hint">
              Нет заблокированных клиентов
            </div>
          ) : (
            blockedClients.map((client) => (
              <ClientCard
                key={client.id}
                client={client}
                onUnblock={() => handleUnblock(client.id)}
              />
            ))
          )}
        </div>
      )}

      {/* Модальное окно создания плана */}
      {showCreatePlanModal && selectedClientId && (
        <CreatePlanModal
          clientId={selectedClientId}
          clientName={selectedClientName}
          onClose={() => setShowCreatePlanModal(false)}
          onCreated={handlePlanCreated}
        />
      )}

      {/* Модальное окно просмотра планов */}
      {showPlansModal && (
        <PlansListModal
          clientName={selectedClientName}
          plans={clientPlans}
          isLoading={isLoadingPlans}
          onClose={() => setShowPlansModal(false)}
          onStatusChange={handleStatusChange}
          onDelete={handleDeletePlan}
          onUpdate={handleUpdatePlan}
        />
      )}

      {/* Модальное окно заморозки */}
      {showFreezeModal && (
        <FreezeModal
          clientName={selectedClientName}
          onFreeze={handleFreeze}
          onClose={() => setShowFreezeModal(false)}
        />
      )}

      {/* Модальное окно блокировки */}
      {showBlockModal && (
        <BlockModal
          clientName={selectedClientName}
          onBlock={handleBlock}
          onClose={() => setShowBlockModal(false)}
        />
      )}
    </div>
  )
}

// Карточка активного клиента
function ClientCard({
  client,
  onApprove,
  onOpenCreatePlan,
  onOpenPlans,
  onFreeze,
  onUnfreeze,
  onBlock,
  onUnblock,
}: {
  client: Client
  onApprove?: () => void
  onOpenCreatePlan?: () => void
  onOpenPlans?: () => void
  onFreeze?: () => void
  onUnfreeze?: () => void
  onBlock?: () => void
  onUnblock?: () => void
}) {
  const [showMenu, setShowMenu] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Закрытие меню при клике вне
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const name = `${client.user.first_name} ${client.user.last_name || ''}`.trim()
  const username = client.user.username ? `@${client.user.username}` : ''
  const isTrial = client.status === 'trial'
  const isFrozen = client.status === 'frozen'
  const isBlocked = client.status === 'blocked'

  // Форматирование даты разморозки
  const freezeUntilDate = client.freeze_until
    ? new Date(client.freeze_until).toLocaleDateString('ru-RU')
    : null

  return (
    <div className="bg-tg-secondary rounded-xl p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center text-xl ${
            isBlocked ? 'bg-red-500/20' : isFrozen ? 'bg-blue-500/20' : 'bg-tg-button/20'
          }`}>
            {isBlocked ? '🚫' : isFrozen ? '❄️' : '👤'}
          </div>
          <div>
            <div className="font-medium text-tg-text">{name}</div>
            {username && <div className="text-sm text-tg-hint">{username}</div>}
            {isTrial && (
              <span className="inline-block px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full mt-1">
                Пробное занятие
              </span>
            )}
            {isFrozen && (
              <div className="text-xs text-blue-500 mt-1">
                {freezeUntilDate ? `Заморожен до ${freezeUntilDate}` : 'Заморожен бессрочно'}
              </div>
            )}
            {isBlocked && (
              <div className="text-xs text-red-500 mt-1">Заблокирован</div>
            )}
            {client.freeze_reason && (isFrozen || isBlocked) && (
              <div className="text-xs text-tg-hint mt-0.5">
                Причина: {client.freeze_reason}
              </div>
            )}
          </div>
        </div>

        {/* Меню действий */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="w-8 h-8 flex items-center justify-center text-tg-hint hover:text-tg-text rounded-lg hover:bg-tg-button/10 transition-colors"
          >
            •••
          </button>

          {showMenu && (
            <div className="absolute right-0 top-10 bg-tg-bg border border-tg-secondary rounded-xl shadow-lg z-10 min-w-[180px] py-1">
              {/* Одобрить trial клиента */}
              {onApprove && (
                <button
                  onClick={() => {
                    setShowMenu(false)
                    onApprove()
                  }}
                  className="w-full px-4 py-2 text-left text-green-600 font-medium hover:bg-tg-secondary transition-colors"
                >
                  ✅ Одобрить
                </button>
              )}

              {/* Планы - только для активных и замороженных */}
              {onOpenCreatePlan && (
                <button
                  onClick={() => {
                    setShowMenu(false)
                    onOpenCreatePlan()
                  }}
                  className="w-full px-4 py-2 text-left text-tg-text hover:bg-tg-secondary transition-colors"
                >
                  Добавить план
                </button>
              )}
              {onOpenPlans && (
                <button
                  onClick={() => {
                    setShowMenu(false)
                    onOpenPlans()
                  }}
                  className="w-full px-4 py-2 text-left text-tg-text hover:bg-tg-secondary transition-colors"
                >
                  Просмотр планов
                </button>
              )}

              {/* Разделитель */}
              {(onOpenCreatePlan || onOpenPlans) && (onFreeze || onUnfreeze || onBlock || onUnblock) && (
                <div className="h-px bg-tg-secondary my-1" />
              )}

              {/* Заморозка */}
              {onFreeze && (
                <button
                  onClick={() => {
                    setShowMenu(false)
                    onFreeze()
                  }}
                  className="w-full px-4 py-2 text-left text-tg-text hover:bg-tg-secondary transition-colors"
                >
                  ❄️ Заморозить
                </button>
              )}

              {/* Разморозка */}
              {onUnfreeze && (
                <button
                  onClick={() => {
                    setShowMenu(false)
                    onUnfreeze()
                  }}
                  className="w-full px-4 py-2 text-left text-green-600 hover:bg-tg-secondary transition-colors"
                >
                  ✅ Разморозить
                </button>
              )}

              {/* Блокировка */}
              {onBlock && (
                <button
                  onClick={() => {
                    setShowMenu(false)
                    onBlock()
                  }}
                  className="w-full px-4 py-2 text-left text-red-500 hover:bg-tg-secondary transition-colors"
                >
                  🚫 Заблокировать
                </button>
              )}

              {/* Разблокировка */}
              {onUnblock && (
                <button
                  onClick={() => {
                    setShowMenu(false)
                    onUnblock()
                  }}
                  className="w-full px-4 py-2 text-left text-green-600 hover:bg-tg-secondary transition-colors"
                >
                  ✅ Разблокировать
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Карточка ожидающего клиента
function PendingClientCard({
  client,
  onApproveTrial,
  onApprove,
  onReject,
}: {
  client: PendingClient
  onApproveTrial: () => void
  onApprove: () => void
  onReject: () => void
}) {
  const name = `${client.user.first_name} ${client.user.last_name || ''}`.trim()
  const username = client.user.username ? `@${client.user.username}` : ''

  return (
    <div className="bg-tg-secondary rounded-xl p-4">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-12 h-12 bg-orange-500/20 rounded-full flex items-center justify-center text-xl">
          ⏳
        </div>
        <div>
          <div className="font-medium text-tg-text">{name}</div>
          {username && <div className="text-sm text-tg-hint">{username}</div>}
        </div>
      </div>
      <div className="flex gap-2 mb-2">
        <Button size="sm" fullWidth variant="secondary" onClick={onApproveTrial}>
          На пробное
        </Button>
        <Button size="sm" fullWidth onClick={onApprove}>
          Принять
        </Button>
      </div>
      <Button variant="outline" size="sm" fullWidth onClick={onReject}>
        Отклонить
      </Button>
    </div>
  )
}

// Модальное окно создания плана
function CreatePlanModal({
  clientId,
  clientName,
  onClose,
  onCreated,
}: {
  clientId: string
  clientName: string
  onClose: () => void
  onCreated: () => void
}) {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [exercises, setExercises] = useState<Exercise[]>([])
  const [imageData, setImageData] = useState<string | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleAddExercise = () => {
    setExercises([...exercises, { name: '' }])
  }

  const handleRemoveExercise = (index: number) => {
    setExercises(exercises.filter((_, i) => i !== index))
  }

  const handleExerciseChange = (
    index: number,
    field: keyof Exercise,
    value: string
  ) => {
    const updated = [...exercises]
    updated[index] = { ...updated[index], [field]: value }
    setExercises(updated)
  }

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Проверка типа файла
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      setError('Поддерживаются только JPEG и PNG')
      return
    }

    // Проверка размера (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Максимальный размер файла 5MB')
      return
    }

    try {
      const base64 = await fileToBase64(file)
      setImageData(base64)
      setImagePreview(base64)
      setError(null)
    } catch (err) {
      setError('Ошибка загрузки изображения')
    }
  }

  const handleSubmit = async () => {
    if (!title.trim()) {
      setError('Введите название плана')
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      // Фильтруем пустые упражнения
      const validExercises = exercises.filter((e) => e.name.trim())

      await createTrainingPlan({
        client_id: clientId,
        title: title.trim(),
        description: description.trim() || undefined,
        exercises: validExercises.length > 0 ? validExercises : undefined,
        image_data: imageData || undefined,
      })

      onCreated()
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Ошибка создания плана')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-tg-bg rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-5">
          <h3 className="text-lg font-semibold text-tg-text mb-1">
            Новый план тренировок
          </h3>
          <p className="text-sm text-tg-hint mb-2">Для: {clientName}</p>
          <p className="text-sm text-tg-hint mb-4">План сохранится неактивным. Чтобы клиент его увидел, включите его в списке планов.</p>

          {error && (
            <div className="bg-red-100 text-red-700 p-3 rounded-lg mb-4 text-sm">
              {error}
            </div>
          )}

          {/* Название */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-tg-text mb-1">
              Название *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Программа на неделю"
              className="w-full p-3 bg-tg-secondary rounded-lg text-tg-text placeholder-tg-hint focus:outline-none focus:ring-2 focus:ring-tg-button"
            />
          </div>

          {/* Описание */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-tg-text mb-1">
              Описание
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Краткое описание плана..."
              rows={3}
              className="w-full p-3 bg-tg-secondary rounded-lg text-tg-text placeholder-tg-hint focus:outline-none focus:ring-2 focus:ring-tg-button resize-none"
            />
          </div>

          {/* Таблица упражнений */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-tg-text">
                Упражнения
              </label>
              <button
                onClick={handleAddExercise}
                className="text-sm text-tg-button font-medium"
              >
                + Добавить
              </button>
            </div>

            {exercises.length > 0 && (
              <div className="space-y-3">
                {exercises.map((ex, index) => (
                  <div
                    key={index}
                    className="bg-tg-secondary rounded-lg p-3 relative"
                  >
                    <button
                      onClick={() => handleRemoveExercise(index)}
                      className="absolute top-2 right-2 text-tg-hint hover:text-red-500 text-sm"
                    >
                      ✕
                    </button>
                    <input
                      type="text"
                      value={ex.name}
                      onChange={(e) =>
                        handleExerciseChange(index, 'name', e.target.value)
                      }
                      placeholder="Название упражнения"
                      className="w-full bg-transparent text-tg-text placeholder-tg-hint mb-2 focus:outline-none font-medium"
                    />
                    <div className="grid grid-cols-4 gap-2">
                      <input
                        type="text"
                        value={ex.sets || ''}
                        onChange={(e) =>
                          handleExerciseChange(index, 'sets', e.target.value)
                        }
                        placeholder="Подходы"
                        className="bg-tg-bg rounded px-2 py-1 text-sm text-tg-text placeholder-tg-hint focus:outline-none"
                      />
                      <input
                        type="text"
                        value={ex.reps || ''}
                        onChange={(e) =>
                          handleExerciseChange(index, 'reps', e.target.value)
                        }
                        placeholder="Повторы"
                        className="bg-tg-bg rounded px-2 py-1 text-sm text-tg-text placeholder-tg-hint focus:outline-none"
                      />
                      <input
                        type="text"
                        value={ex.weight || ''}
                        onChange={(e) =>
                          handleExerciseChange(index, 'weight', e.target.value)
                        }
                        placeholder="Вес"
                        className="bg-tg-bg rounded px-2 py-1 text-sm text-tg-text placeholder-tg-hint focus:outline-none"
                      />
                      <input
                        type="text"
                        value={ex.rest || ''}
                        onChange={(e) =>
                          handleExerciseChange(index, 'rest', e.target.value)
                        }
                        placeholder="Отдых"
                        className="bg-tg-bg rounded px-2 py-1 text-sm text-tg-text placeholder-tg-hint focus:outline-none"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Загрузка изображения */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-tg-text mb-2">
              Изображение
            </label>
            {imagePreview ? (
              <div className="relative">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="w-full h-48 object-cover rounded-lg"
                />
                <button
                  onClick={() => {
                    setImageData(null)
                    setImagePreview(null)
                  }}
                  className="absolute top-2 right-2 bg-black/50 text-white w-6 h-6 rounded-full flex items-center justify-center"
                >
                  ✕
                </button>
              </div>
            ) : (
              <label className="block w-full p-6 border-2 border-dashed border-tg-secondary rounded-lg text-center cursor-pointer hover:border-tg-button transition-colors">
                <input
                  type="file"
                  accept="image/jpeg,image/png"
                  onChange={handleImageChange}
                  className="hidden"
                />
                <div className="text-tg-hint">
                  <div className="text-2xl mb-1">📷</div>
                  <div className="text-sm">Нажмите для загрузки</div>
                  <div className="text-xs mt-1">JPEG, PNG до 5MB</div>
                </div>
              </label>
            )}
          </div>

          {/* Кнопки */}
          <div className="flex gap-3">
            <Button variant="outline" fullWidth onClick={onClose}>
              Отмена
            </Button>
            <Button fullWidth onClick={handleSubmit} loading={isSubmitting}>
              Создать
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Модальное окно списка планов
function PlansListModal({
  clientName,
  plans,
  isLoading,
  onClose,
  onStatusChange,
  onDelete,
  onUpdate,
}: {
  clientName: string
  plans: TrainingPlan[]
  isLoading: boolean
  onClose: () => void
  onStatusChange: (planId: string, status: 'active' | 'inactive') => void
  onDelete: (planId: string) => void
  onUpdate: (planId: string, data: UpdatePlanData) => Promise<void>
}) {
  const [selectedPlan, setSelectedPlan] = useState<TrainingPlan | null>(null)
  const [editingPlan, setEditingPlan] = useState<TrainingPlan | null>(null)

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-tg-bg rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-tg-text">
                Планы тренировок
              </h3>
              <p className="text-sm text-tg-hint">{clientName}</p>
            </div>
            <button
              onClick={onClose}
              className="text-tg-hint hover:text-tg-text text-xl"
            >
              ✕
            </button>
          </div>

          {isLoading ? (
            <div className="text-center py-8 text-tg-hint">Загрузка...</div>
          ) : plans.length === 0 ? (
            <div className="text-center py-8 text-tg-hint">
              Нет планов тренировок
            </div>
          ) : (
            <div className="space-y-3">
              {plans.map((plan) => (
                <div
                  key={plan.id}
                  className="bg-tg-secondary rounded-lg p-3"
                >
                  <div className="flex items-start gap-3">
                    {plan.image_data && (
                      <img
                        src={plan.image_data}
                        alt={plan.title}
                        className="w-16 h-16 object-cover rounded-lg cursor-pointer"
                        onClick={() => setSelectedPlan(plan)}
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="font-medium text-tg-text truncate">
                          {plan.title}
                        </div>
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${
                            plan.status === 'active'
                              ? 'bg-green-500/20 text-green-600'
                              : 'bg-gray-500/20 text-gray-500'
                          }`}
                        >
                          {plan.status === 'active' ? 'Активный' : 'Неактивный'}
                        </span>
                      </div>
                      {plan.description && (
                        <div className="text-sm text-tg-hint mt-1 line-clamp-2">
                          {plan.description}
                        </div>
                      )}
                      <div className="text-xs text-tg-hint mt-1">
                        {new Date(plan.created_at).toLocaleDateString('ru-RU')}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3 flex-wrap">
                    <button
                      onClick={() => setSelectedPlan(plan)}
                      className="text-sm text-tg-button font-medium"
                    >
                      Открыть
                    </button>
                    <button
                      onClick={() => setEditingPlan(plan)}
                      className="text-sm text-tg-button font-medium"
                    >
                      Редактировать
                    </button>
                    <button
                      onClick={() =>
                        onStatusChange(
                          plan.id,
                          plan.status === 'active' ? 'inactive' : 'active'
                        )
                      }
                      className="text-sm text-tg-hint font-medium"
                    >
                      {plan.status === 'active' ? 'Выключить' : 'Включить'}
                    </button>
                    <button
                      onClick={() => onDelete(plan.id)}
                      className="text-sm text-red-500 font-medium"
                    >
                      Удалить
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Просмотр плана */}
      {selectedPlan && (
        <PlanViewerModal
          plan={selectedPlan}
          onClose={() => setSelectedPlan(null)}
        />
      )}

      {/* Редактирование плана */}
      {editingPlan && (
        <EditPlanModal
          plan={editingPlan}
          onClose={() => setEditingPlan(null)}
          onSave={async (data) => {
            await onUpdate(editingPlan.id, data)
            setEditingPlan(null)
          }}
        />
      )}
    </div>
  )
}

// Модальное окно просмотра плана
function PlanViewerModal({
  plan,
  onClose,
}: {
  plan: TrainingPlan
  onClose: () => void
}) {
  return (
    <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-[60] p-4">
      <div className="bg-tg-bg rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-tg-text">{plan.title}</h3>
              <span
                className={`text-xs px-2 py-0.5 rounded-full ${
                  plan.status === 'active'
                    ? 'bg-green-500/20 text-green-600'
                    : 'bg-gray-500/20 text-gray-500'
                }`}
              >
                {plan.status === 'active' ? 'Активный' : 'Неактивный'}
              </span>
            </div>
            <button
              onClick={onClose}
              className="text-tg-hint hover:text-tg-text text-xl"
            >
              ✕
            </button>
          </div>

          {plan.image_data && (
            <img
              src={plan.image_data}
              alt={plan.title}
              className="w-full rounded-lg mb-4"
            />
          )}

          {plan.description && (
            <p className="text-tg-text mb-4">{plan.description}</p>
          )}

          {plan.exercises && plan.exercises.length > 0 && (
            <div>
              <h4 className="font-medium text-tg-text mb-2">Упражнения:</h4>
              <div className="space-y-2">
                {plan.exercises.map((ex, index) => (
                  <div
                    key={index}
                    className="bg-tg-secondary rounded-lg p-3"
                  >
                    <div className="font-medium text-tg-text">{ex.name}</div>
                    <div className="flex gap-3 mt-1 text-sm text-tg-hint">
                      {ex.sets && <span>{ex.sets} подходов</span>}
                      {ex.reps && <span>{ex.reps} повторений</span>}
                      {ex.weight && <span>{ex.weight}</span>}
                      {ex.rest && <span>отдых {ex.rest}</span>}
                    </div>
                    {ex.notes && (
                      <div className="text-sm text-tg-hint mt-1">{ex.notes}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Модальное окно редактирования плана
function EditPlanModal({
  plan,
  onClose,
  onSave,
}: {
  plan: TrainingPlan
  onClose: () => void
  onSave: (data: UpdatePlanData) => Promise<void>
}) {
  const [title, setTitle] = useState(plan.title)
  const [description, setDescription] = useState(plan.description || '')
  const [exercises, setExercises] = useState<Exercise[]>(plan.exercises || [])
  const [imageData, setImageData] = useState<string | null>(plan.image_data || null)
  const [imagePreview, setImagePreview] = useState<string | null>(plan.image_data || null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleAddExercise = () => {
    setExercises([...exercises, { name: '' }])
  }

  const handleRemoveExercise = (index: number) => {
    setExercises(exercises.filter((_, i) => i !== index))
  }

  const handleExerciseChange = (
    index: number,
    field: keyof Exercise,
    value: string
  ) => {
    const updated = [...exercises]
    updated[index] = { ...updated[index], [field]: value }
    setExercises(updated)
  }

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      setError('Поддерживаются только JPEG и PNG')
      return
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Максимальный размер файла 5MB')
      return
    }

    try {
      const base64 = await fileToBase64(file)
      setImageData(base64)
      setImagePreview(base64)
      setError(null)
    } catch (err) {
      setError('Ошибка загрузки изображения')
    }
  }

  const handleRemoveImage = () => {
    setImageData(null)
    setImagePreview(null)
  }

  const handleSubmit = async () => {
    if (!title.trim()) {
      setError('Введите название плана')
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      const validExercises = exercises.filter((e) => e.name.trim())

      await onSave({
        title: title.trim(),
        description: description.trim() || undefined,
        exercises: validExercises.length > 0 ? validExercises : undefined,
        image_data: imageData || undefined,
      })
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Ошибка обновления плана')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4">
      <div className="bg-tg-bg rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-5">
          <h3 className="text-lg font-semibold text-tg-text mb-4">
            Редактирование плана
          </h3>

          {error && (
            <div className="bg-red-100 text-red-700 p-3 rounded-lg mb-4 text-sm">
              {error}
            </div>
          )}

          {/* Название */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-tg-text mb-1">
              Название *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Программа на неделю"
              className="w-full p-3 bg-tg-secondary rounded-lg text-tg-text placeholder-tg-hint focus:outline-none focus:ring-2 focus:ring-tg-button"
            />
          </div>

          {/* Описание */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-tg-text mb-1">
              Описание
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Краткое описание плана..."
              rows={3}
              className="w-full p-3 bg-tg-secondary rounded-lg text-tg-text placeholder-tg-hint focus:outline-none focus:ring-2 focus:ring-tg-button resize-none"
            />
          </div>

          {/* Таблица упражнений */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-tg-text">
                Упражнения
              </label>
              <button
                onClick={handleAddExercise}
                className="text-sm text-tg-button font-medium"
              >
                + Добавить
              </button>
            </div>

            {exercises.length > 0 && (
              <div className="space-y-3">
                {exercises.map((ex, index) => (
                  <div
                    key={index}
                    className="bg-tg-secondary rounded-lg p-3 relative"
                  >
                    <button
                      onClick={() => handleRemoveExercise(index)}
                      className="absolute top-2 right-2 text-tg-hint hover:text-red-500 text-sm"
                    >
                      ✕
                    </button>
                    <input
                      type="text"
                      value={ex.name}
                      onChange={(e) =>
                        handleExerciseChange(index, 'name', e.target.value)
                      }
                      placeholder="Название упражнения"
                      className="w-full bg-transparent text-tg-text placeholder-tg-hint mb-2 focus:outline-none font-medium"
                    />
                    <div className="grid grid-cols-4 gap-2">
                      <input
                        type="text"
                        value={ex.sets || ''}
                        onChange={(e) =>
                          handleExerciseChange(index, 'sets', e.target.value)
                        }
                        placeholder="Подходы"
                        className="bg-tg-bg rounded px-2 py-1 text-sm text-tg-text placeholder-tg-hint focus:outline-none"
                      />
                      <input
                        type="text"
                        value={ex.reps || ''}
                        onChange={(e) =>
                          handleExerciseChange(index, 'reps', e.target.value)
                        }
                        placeholder="Повторы"
                        className="bg-tg-bg rounded px-2 py-1 text-sm text-tg-text placeholder-tg-hint focus:outline-none"
                      />
                      <input
                        type="text"
                        value={ex.weight || ''}
                        onChange={(e) =>
                          handleExerciseChange(index, 'weight', e.target.value)
                        }
                        placeholder="Вес"
                        className="bg-tg-bg rounded px-2 py-1 text-sm text-tg-text placeholder-tg-hint focus:outline-none"
                      />
                      <input
                        type="text"
                        value={ex.rest || ''}
                        onChange={(e) =>
                          handleExerciseChange(index, 'rest', e.target.value)
                        }
                        placeholder="Отдых"
                        className="bg-tg-bg rounded px-2 py-1 text-sm text-tg-text placeholder-tg-hint focus:outline-none"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Загрузка изображения */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-tg-text mb-2">
              Изображение
            </label>
            {imagePreview ? (
              <div className="relative">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="w-full h-48 object-cover rounded-lg"
                />
                <div className="absolute top-2 right-2 flex gap-2">
                  <label className="bg-black/50 text-white w-8 h-8 rounded-full flex items-center justify-center cursor-pointer hover:bg-black/70">
                    <input
                      type="file"
                      accept="image/jpeg,image/png"
                      onChange={handleImageChange}
                      className="hidden"
                    />
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                  </label>
                  <button
                    onClick={handleRemoveImage}
                    className="bg-black/50 text-white w-8 h-8 rounded-full flex items-center justify-center hover:bg-black/70"
                  >
                    ✕
                  </button>
                </div>
              </div>
            ) : (
              <label className="block w-full p-6 border-2 border-dashed border-tg-secondary rounded-lg text-center cursor-pointer hover:border-tg-button transition-colors">
                <input
                  type="file"
                  accept="image/jpeg,image/png"
                  onChange={handleImageChange}
                  className="hidden"
                />
                <div className="text-tg-hint">
                  <div className="text-2xl mb-1">📷</div>
                  <div className="text-sm">Нажмите для загрузки</div>
                  <div className="text-xs mt-1">JPEG, PNG до 5MB</div>
                </div>
              </label>
            )}
          </div>

          {/* Кнопки */}
          <div className="flex gap-3">
            <Button variant="outline" fullWidth onClick={onClose}>
              Отмена
            </Button>
            <Button fullWidth onClick={handleSubmit} loading={isSubmitting}>
              Сохранить
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Модальное окно заморозки клиента
function FreezeModal({
  clientName,
  onFreeze,
  onClose,
}: {
  clientName: string
  onFreeze: (days?: number, reason?: string) => void
  onClose: () => void
}) {
  const [days, setDays] = useState<string>('')
  const [reason, setReason] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async () => {
    setIsSubmitting(true)
    try {
      const daysNum = days ? parseInt(days, 10) : undefined
      await onFreeze(daysNum, reason || undefined)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-tg-bg rounded-xl w-full max-w-sm">
        <div className="p-5">
          <h3 className="text-lg font-semibold text-tg-text mb-1">
            ❄️ Заморозить клиента
          </h3>
          <p className="text-sm text-tg-hint mb-4">{clientName}</p>

          <p className="text-sm text-tg-text mb-4">
            Замороженный клиент может записаться на тренировку — это автоматически снимет заморозку.
          </p>

          {/* Количество дней */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-tg-text mb-1">
              Количество дней (опционально)
            </label>
            <input
              type="number"
              value={days}
              onChange={(e) => setDays(e.target.value)}
              placeholder="Бессрочно"
              min="1"
              className="w-full p-3 bg-tg-secondary rounded-lg text-tg-text placeholder-tg-hint focus:outline-none focus:ring-2 focus:ring-tg-button"
            />
            <p className="text-xs text-tg-hint mt-1">
              Оставьте пустым для бессрочной заморозки
            </p>
          </div>

          {/* Причина */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-tg-text mb-1">
              Причина (опционально)
            </label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Отпуск, болезнь..."
              className="w-full p-3 bg-tg-secondary rounded-lg text-tg-text placeholder-tg-hint focus:outline-none focus:ring-2 focus:ring-tg-button"
            />
          </div>

          {/* Кнопки */}
          <div className="flex gap-3">
            <Button variant="outline" fullWidth onClick={onClose}>
              Отмена
            </Button>
            <Button fullWidth onClick={handleSubmit} loading={isSubmitting}>
              Заморозить
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Модальное окно блокировки клиента
function BlockModal({
  clientName,
  onBlock,
  onClose,
}: {
  clientName: string
  onBlock: (reason?: string) => void
  onClose: () => void
}) {
  const [reason, setReason] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [confirmed, setConfirmed] = useState(false)

  const handleSubmit = async () => {
    if (!confirmed) {
      setConfirmed(true)
      return
    }

    setIsSubmitting(true)
    try {
      await onBlock(reason || undefined)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-tg-bg rounded-xl w-full max-w-sm">
        <div className="p-5">
          <h3 className="text-lg font-semibold text-tg-text mb-1">
            🚫 Заблокировать клиента
          </h3>
          <p className="text-sm text-tg-hint mb-4">{clientName}</p>

          {!confirmed ? (
            <>
              <div className="bg-red-100 text-red-700 p-4 rounded-lg mb-4">
                <p className="font-medium mb-2">Внимание!</p>
                <p className="text-sm">
                  Заблокированный клиент не сможет записываться на тренировки.
                  Только вы сможете его разблокировать.
                </p>
              </div>

              <div className="flex gap-3">
                <Button variant="outline" fullWidth onClick={onClose}>
                  Отмена
                </Button>
                <Button
                  fullWidth
                  onClick={handleSubmit}
                  className="!bg-red-500 hover:!bg-red-600"
                >
                  Продолжить
                </Button>
              </div>
            </>
          ) : (
            <>
              {/* Причина */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-tg-text mb-1">
                  Причина (опционально)
                </label>
                <input
                  type="text"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Нарушение правил..."
                  className="w-full p-3 bg-tg-secondary rounded-lg text-tg-text placeholder-tg-hint focus:outline-none focus:ring-2 focus:ring-tg-button"
                />
              </div>

              {/* Кнопки */}
              <div className="flex gap-3">
                <Button variant="outline" fullWidth onClick={onClose}>
                  Отмена
                </Button>
                <Button
                  fullWidth
                  onClick={handleSubmit}
                  loading={isSubmitting}
                  className="!bg-red-500 hover:!bg-red-600"
                >
                  Заблокировать
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
